import React from "react";

import { Chart } from "./charts";
import { chartAxisNumber, chartNumber } from "../utils/format";
import { chartRangeBounds, filterChartSeries, pointDate } from "../utils/chartRange";
import { alignDatedValues, rebaseComparisonValues } from "../utils/performanceComparison";
import {
  mergeSameDayEvents,
  performanceEventsWithinRange,
  visiblePerformanceAssetEvents,
} from "../utils/performanceEvents";

function chartPercent(value) {
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) return "--";
  return `${(numberValue * 100).toLocaleString("zh-CN", { maximumFractionDigits: 2 })}%`;
}

function chartIndexNumber(value) {
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) return "--";
  return numberValue.toLocaleString("zh-CN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function nicePercentAxisMin(values) {
  if (!values.length) return 0;
  const min = Math.min(...values);
  if (min >= 0) return -0.05;
  return Math.floor((min * 100) / 10) * 10 / 100;
}

function nicePercentAxisMax(values) {
  if (!values.length) return 0.1;
  const max = Math.max(...values);
  if (max <= 0) return 0;
  return Math.ceil((max * 100) / 10) * 10 / 100;
}

// 日 K：红涨绿跌，和全站 --semantic-up / --semantic-down 一致。
const CANDLE_UP = "#d63f47";
const CANDLE_DOWN = "#00966a";
const hasOhlc = (point) => ["open", "high", "low", "value"].every((key) => Number.isFinite(point?.[key]));

const PERFORMANCE_COLORS = {
  price: "#354052",
  profit: "#0b8f5b",
  benchmark: "#356dff",
  compare: "#d97706",
  buy: "#0b8f5b",
  sell: "#d44f4a",
  dividend: "#c77914",
  shares: "#7c5bd6",
  opening: "#0f766e",
  mixedTrade: "#52645b",
};

function rebaseValues(values, kind, percentMode) {
  return rebaseComparisonValues(values, { keepAbsolute: !percentMode && kind === "price" });
}

// 同一天撞上多种事件时，标记文字按这个顺序拼接（买卖在前、录入持仓其次、
// 分红送转配股在后），不然拼接顺序看数组里谁先来，同一种组合今天显示
// 「持/卖」明天可能显示「卖/持」。
const MARK_PRIORITY = ["买", "申", "卖", "赎", "持", "分", "配", "送"];

function sortMarks(marks) {
  return [...marks].sort((left, right) => MARK_PRIORITY.indexOf(left) - MARK_PRIORITY.indexOf(right));
}

function performanceEvent(event, isFund) {
  const action = String(event?.action || event?.event_type || "").toUpperCase();
  if (["BUY", "SUBSCRIBE"].includes(action)) {
    return { mark: isFund ? "申" : "买", label: isFund ? "申购" : "买入", color: PERFORMANCE_COLORS.buy };
  }
  if (["SELL", "REDEEM"].includes(action)) {
    return { mark: isFund ? "赎" : "卖", label: isFund ? "赎回" : "卖出", color: PERFORMANCE_COLORS.sell };
  }
  if (action === "OPENING_POSITION") {
    // 录入持仓不是成交：没有对手方、没有手续费，只是用户声明从这天起持有多少。
    // 借「买」的绿会让人以为那天买了一笔，所以另给一个颜色和字。
    return { mark: "持", label: "录入持仓", color: PERFORMANCE_COLORS.opening };
  }
  if (action.startsWith("CASH_DIVIDEND")) {
    // 标在股权登记日：那天收盘持有才有份，核算也是按这天判定（用户 2026-09-16）
    return { mark: "分", label: "股权登记", color: PERFORMANCE_COLORS.dividend };
  }
  return { mark: action.includes("RIGHT") ? "配" : "送", label: action.includes("RIGHT") ? "配股" : "送转", color: PERFORMANCE_COLORS.shares };
}

function eventDetail(event, descriptor) {
  const quantity = Number(event?.quantity ?? event?.quantity_delta);
  const price = Number(event?.price);
  const cash = Number(event?.cash_delta ?? event?.receivable_delta);
  const cashPerShare = Number(event?.cash_per_share);
  const details = [];
  if (descriptor.mark === "分" && Number.isFinite(cashPerShare) && cashPerShare > 0) {
    details.push(`每股 ${cashPerShare.toLocaleString("zh-CN", { maximumFractionDigits: 6 })} 元`);
  }
  if (descriptor.mark === "分" && event?.pay_date) details.push(`派息日 ${event.pay_date}`);
  // 标记挪到登记日之后，公告日在 tooltip 里补回来，信息不丢
  const announced = event?.announcement_date ?? event?.ann_date;
  if (descriptor.mark === "分" && announced) details.push(`公告 ${announced}`);
  const unit = event?.quantity_unit || (descriptor.mark === "申" || descriptor.mark === "赎" ? "份" : "股");
  if (Number.isFinite(quantity) && quantity !== 0) details.push(`${Math.abs(quantity).toLocaleString("zh-CN", { maximumFractionDigits: 4 })} ${unit}`);
  // 录入持仓那一条里的 price 是成本价，不是成交价，写成「@ x 元」会读成成交。
  if (Number.isFinite(price) && price > 0) details.push(`${descriptor.mark === "持" ? "成本价 " : "@ "}${price.toLocaleString("zh-CN", { maximumFractionDigits: 4 })} 元`);
  if (Number.isFinite(cash) && cash !== 0) details.push(`${cash > 0 ? "+" : ""}${cash.toLocaleString("zh-CN", { maximumFractionDigits: 2 })} 元`);
  return details.join(" · ") || event?.title || descriptor.label;
}

/** Result-page comparison chart. Every non-price-only view is rebased at the
 * selected window start; this is visual comparison only and never mutates the
 * deterministic accounting result shown above the chart. */
export function AssetPerformanceChart({
  priceSeries = [], profitSeries = [], benchmarkSeries = [], additionalSeries = [],
  chartRange = "all", showPrice = false, showProfit = true, showBenchmark = true,
  assetName = "标的价格", benchmarkName = "基准", transactions = [], assetEvents = [],
  isFund = false, candles = false,
}) {
  const option = React.useMemo(() => {
    const datedBenchmark = benchmarkSeries.map((point) => ({ ...point, date: pointDate(point) || point.t }));
    const visiblePrice = filterChartSeries(priceSeries, chartRange);
    const visibleProfit = filterChartSeries(profitSeries, chartRange);
    const visibleBenchmark = filterChartSeries(datedBenchmark, chartRange);
    const visibleAdditional = additionalSeries.map((item) => ({
      ...item,
      points: filterChartSeries((item.points ?? []).map((point) => ({ ...point, date: pointDate(point) || point.t })), chartRange),
    }));
    const priceByDate = new Map(visiblePrice.map((point) => [pointDate(point), point]));
    // 日 K 只在「只看价格（可带基准）」的绝对价格视图下画：叠了盈亏或对比标的就是百分比视图，蜡烛没有意义；
    // 场外基金只有净值，不画。
    const candleMode = candles && showPrice && !showProfit && !isFund && visibleAdditional.length === 0 && visiblePrice.some(hasOhlc);
    const dateSet = new Set();
    // 价格/净值线关闭时仍保留它的日期域，避免“全部”退回首次持仓日。
    visiblePrice.forEach((point) => dateSet.add(pointDate(point)));
    if (showProfit) visibleProfit.forEach((point) => dateSet.add(pointDate(point)));
    if (showBenchmark) visibleBenchmark.forEach((point) => dateSet.add(pointDate(point)));
    visibleAdditional.forEach((item) => item.points.forEach((point) => dateSet.add(pointDate(point))));
    const dates = [...dateSet].filter(Boolean).sort();
    if (!dates.length) return null;
    const activeCount = Number(showPrice) + Number(showProfit) + Number(showBenchmark && visibleBenchmark.length > 0) + visibleAdditional.length;
    const dualAxisMode = showPrice && !showProfit && showBenchmark && visibleBenchmark.length > 0 && visibleAdditional.length === 0;
    const percentMode = !dualAxisMode && (activeCount > 1 || showProfit || !showPrice);
    const definitions = [];
    if (showPrice) definitions.push({ key: "price", name: assetName, color: PERFORMANCE_COLORS.price, values: alignDatedValues(visiblePrice, dates) });
    if (showProfit) definitions.push({ key: "profit", name: "持仓盈亏率", color: PERFORMANCE_COLORS.profit, values: alignDatedValues(visibleProfit, dates, { leadingValue: 1 }), dashed: !showPrice });
    if (showBenchmark && visibleBenchmark.length) definitions.push({ key: "benchmark", name: benchmarkName, color: PERFORMANCE_COLORS.benchmark, values: alignDatedValues(visibleBenchmark, dates), dashed: true });
    visibleAdditional.forEach((item, index) => definitions.push({
      key: "compare",
      name: item.name,
      color: item.color ?? [PERFORMANCE_COLORS.compare, "#7c5bd6"][index % 2],
      values: alignDatedValues(item.points, dates),
      dashed: item.dashed,
    }));
    const normalized = definitions.map((item) => ({
      ...item,
      values: dualAxisMode ? item.values : rebaseValues(item.values, item.key, percentMode),
    }));
    const anchor = normalized.find((item) => item.key === "price") ?? normalized.find((item) => item.key === "profit");
    const anchorByDate = new Map(dates.map((date, index) => [date, anchor?.values[index]]));
    const selectedBounds = chartRangeBounds(dates.at(-1), chartRange);
    const eventWindow = {
      startDate: selectedBounds.startDate ?? dates[0],
      endDate: selectedBounds.endDate ?? dates.at(-1),
    };
    const rawEvents = performanceEventsWithinRange([
      ...transactions.map((transaction) => ({ ...transaction, date: transaction.trade_date ?? transaction.date })),
      ...visiblePerformanceAssetEvents(assetEvents),
    ], eventWindow);
    // 同一天不管撞上几种事件（交易、录入持仓、分红、送转配股……）都只画一个
    // 点：只有一种字（哪怕好几条，比如一天卖两次）用那个字、明细逐条列；
    // 两种以上字拼接显示，比如「持/卖」（2026-09-17，见 mergeSameDayEvents 注释）。
    const markers = mergeSameDayEvents(rawEvents, (event) => performanceEvent(event, isFund).mark).map((group) => {
      const displayDate = dates.find((date) => (
        date >= group.date && Number.isFinite(anchorByDate.get(date))
      ));
      const value = displayDate ? anchorByDate.get(displayDate) : null;
      if (!displayDate || !Number.isFinite(value)) return null;
      const descriptorsByMark = new Map();
      group.events.forEach((event) => {
        const descriptor = performanceEvent(event, isFund);
        if (!descriptorsByMark.has(descriptor.mark)) descriptorsByMark.set(descriptor.mark, descriptor);
      });
      const mixed = group.marks.length > 1;
      const orderedMarks = mixed ? sortMarks(group.marks) : group.marks;
      const mark = orderedMarks.join("/");
      const detail = group.events.length > 1
        ? group.events.map((event) => {
          const descriptor = performanceEvent(event, isFund);
          return `${descriptor.label}：${eventDetail(event, descriptor)}`;
        }).join("<br/>")
        : eventDetail(group.events[0], descriptorsByMark.get(orderedMarks[0]));
      return {
        coord: [displayDate, value],
        value: mark,
        symbol: mixed ? "roundRect" : "circle",
        symbolSize: mixed ? [24 + mark.length * 9, 22] : 21,
        symbolOffset: mixed ? [0, -15] : undefined,
        itemStyle: { color: mixed ? PERFORMANCE_COLORS.mixedTrade : descriptorsByMark.get(orderedMarks[0]).color, borderColor: "#fff", borderWidth: 2 },
        label: { show: true, formatter: mark, color: "#fff", fontWeight: 800, fontSize: 10 },
        eventLabel: orderedMarks.map((m) => descriptorsByMark.get(m).label).join(" / "),
        eventDetail: detail,
        eventDate: group.date,
      };
    }).filter(Boolean);
    const values = normalized.flatMap((item) => item.values).filter(Number.isFinite);
    return {
      animation: false,
      grid: { left: 52, right: dualAxisMode ? 58 : 22, top: 18, bottom: 34 },
      tooltip: {
        trigger: "axis",
        borderColor: "#d7e2dc",
        backgroundColor: "rgba(255,255,255,.98)",
        textStyle: { color: "#34443b", fontSize: 12 },
        formatter: (params) => {
          const items = Array.isArray(params) ? params : [];
          const lines = [`<strong>${items[0]?.axisValue ?? "—"}</strong>`];
          items.forEach((item) => {
            if (item.seriesType === "candlestick") {
              const bar = priceByDate.get(item.axisValue);
              lines.push(
                `${item.marker}${item.seriesName}（日K，不复权）`,
                `开盘：${chartNumber(bar?.open)}　收盘：${chartNumber(bar?.value)}`,
                `最高：${chartNumber(bar?.high)}　最低：${chartNumber(bar?.low)}`,
              );
              return;
            }
            const definition = normalized[item.seriesIndex];
            const value = percentMode
              ? chartPercent(item.value)
              : definition?.key === "benchmark"
                ? chartIndexNumber(item.value)
                : chartNumber(item.value);
            lines.push(`${item.marker}${item.seriesName}：${value}`);
          });
          return lines.join("<br/>");
        },
      },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: dates,
        // The default value-axis behavior places the category axis on y=0.
        // That clipped zero-value trade markers and made the range controls
        // look like part of the plot. Keep dates at the physical chart bottom.
        axisLine: { onZero: false },
        axisLabel: { formatter: (value) => String(value).slice(0, 7), hideOverlap: true },
      },
      yAxis: dualAxisMode ? [
        {
          type: "value",
          scale: true,
          axisLabel: { formatter: chartAxisNumber },
          splitLine: { lineStyle: { color: "#edf2ef" } },
        },
        {
          type: "value",
          scale: true,
          position: "right",
          axisLabel: { formatter: chartAxisNumber },
          splitLine: { show: false },
        },
      ] : {
        type: "value",
        scale: true,
        min: percentMode ? nicePercentAxisMin(values) : undefined,
        max: percentMode ? nicePercentAxisMax(values) : undefined,
        axisLabel: { formatter: percentMode ? chartPercent : chartAxisNumber },
        splitLine: { lineStyle: { color: "#edf2ef" } },
      },
      series: normalized.map((item) => (candleMode && item.key === "price" ? {
        name: item.name,
        type: "candlestick",
        data: dates.map((date) => {
          const bar = priceByDate.get(date);
          return hasOhlc(bar) ? [bar.open, bar.value, bar.low, bar.high] : "-";  // ECharts 顺序：开、收、低、高
        }),
        itemStyle: { color: CANDLE_UP, color0: CANDLE_DOWN, borderColor: CANDLE_UP, borderColor0: CANDLE_DOWN },
        markPoint: {
          symbolKeepAspect: true,
          data: markers,
          tooltip: {
            trigger: "item",
            formatter: ({ data }) => `<strong>${data.eventDate} · ${data.eventLabel}</strong><br/>${data.eventDetail}`,
          },
        },
      } : {
        name: item.name,
        type: "line",
        yAxisIndex: dualAxisMode && item.key === "benchmark" ? 1 : 0,
        data: item.values,
        showSymbol: false,
        connectNulls: false,
        smooth: false,
        lineStyle: { color: item.color, width: item.key === "profit" ? 2.4 : 2, type: item.dashed ? [5, 4] : "solid" },
        itemStyle: { color: item.color },
        markPoint: item === anchor ? {
          symbolKeepAspect: true,
          data: markers,
          tooltip: {
            trigger: "item",
            formatter: ({ data }) => `<strong>${data.eventDate} · ${data.eventLabel}</strong><br/>${data.eventDetail}`,
          },
        } : undefined,
      })),
    };
  }, [candles, priceSeries, profitSeries, benchmarkSeries, additionalSeries, chartRange, showPrice, showProfit, showBenchmark, assetName, benchmarkName, transactions, assetEvents, isFund]);
  if (!option) return <div className="chart-empty">当前时间范围没有可展示的价格或核算序列。</div>;
  return <Chart option={option} />;
}
